import { IconShieldCheck } from "@tabler/icons-react";
import { useState } from "react";
import { api, type Me } from "../api";
import { useSession } from "../session";
import { ErrorNote } from "./ui";

/** True when the identity accepted the service terms on this brand. */
export function hasServiceConsent(me: Me, brandCode: string): boolean {
  return me.consents.some((c) => c.brand === brandCode && c.purpose === "service" && c.given);
}

/**
 * The one-time card on Home after the first sign-in (docs/16 §6, decision 4): the brands are one family with one
 * controller; "I understand" records the service consent for this brand, the marketing boxes are optional and per brand.
 */
export function ConsentCard({ me }: { me: Me }) {
  const { brand, brandInfo, lang, refresh, setMe, t } = useSession();
  const [busy, setBusy] = useState(false);
  const [marketingBusy, setMarketingBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!brand || done || me.session.kind !== "full" || hasServiceConsent(me, brand.code)) return null;

  const brands = brandInfo?.brands ?? [];
  const statement = brand.groupStatement[lang] ?? brand.groupStatement.en ?? "";
  const marketingOf = (code: string) => me.consents.some((c) => c.brand === code && c.purpose === "marketing" && c.given);

  const understand = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.setConsent({ brand: brand.code, purpose: "service", given: true });
      setDone(true);
      await refresh();
    } catch {
      setError(t("somethingWrong"));
    } finally {
      setBusy(false);
    }
  };

  const toggleMarketing = async (code: string, given: boolean) => {
    setMarketingBusy(code);
    setError(null);
    try {
      const res = await api.setConsent({ brand: code, purpose: "marketing", given });
      setMe({ ...me, consents: res.consents });
    } catch {
      setError(t("somethingWrong"));
    } finally {
      setMarketingBusy(null);
    }
  };

  return (
    <section className="card consent-card" aria-labelledby="consent-title">
      <div className="consent-head">
        <span className="list-icon">
          <IconShieldCheck size={20} stroke={1.75} />
        </span>
        <h2 id="consent-title">{t("consentTitle")}</h2>
      </div>
      <p className="small">{statement}</p>
      <p className="small muted">
        {t("consentController")} <a href="/privacy">{t("readPrivacy")}</a>
      </p>
      {error && <ErrorNote>{error}</ErrorNote>}
      <button type="button" className="button button-primary button-block" disabled={busy} onClick={() => void understand()}>
        {busy ? t("saving") : t("iUnderstand")}
      </button>
      {brands.length > 0 && (
        <div className="consent-marketing">
          <p className="notices-subtitle">{t("consentMarketingTitle")}</p>
          <ul className="list">
            {brands.map((b) => (
              <li key={b.code} className="list-row">
                <label className="checkbox">
                  <input type="checkbox" checked={marketingOf(b.code)} disabled={marketingBusy === b.code} onChange={(e) => void toggleMarketing(b.code, e.target.checked)} />
                  <span className="small">{t("marketingFrom", { brand: b.name })}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
